(() => {
  const root = document.documentElement;
  if (root.dataset.holuboLightboxInitialized) return;
  root.dataset.holuboLightboxInitialized = 'true';

  const triggerSelector = 'a[data-hc-lightbox], a[data-holubo-lightbox]';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  let viewer;
  let triggers = [];
  let currentIndex = 0;
  let returnFocus;
  let requestId = 0;
  let closeTimer;
  let scrollState;
  let ignoreBackdropClickUntil = 0;

  // Fixed-body locking also prevents background scrolling on mobile Safari.
  const lockScroll = () => {
    const properties = ['position', 'top', 'left', 'width', 'overflow', 'padding-right'];
    const body = document.body;
    scrollState = {
      x: window.scrollX,
      y: window.scrollY,
      overflow: root.style.getPropertyValue('overflow'),
      overflowPriority: root.style.getPropertyPriority('overflow'),
      properties: properties.map(name => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)])
    };
    const scrollbar = window.innerWidth - root.clientWidth;
    const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.position = 'fixed';
    body.style.top = `-${scrollState.y}px`;
    body.style.left = `-${scrollState.x}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${padding + scrollbar}px`;
    root.style.overflow = 'hidden';
  };

  const unlockScroll = () => {
    if (!scrollState) return;
    const state = scrollState;
    scrollState = null;
    state.properties.forEach(([name, value, priority]) => {
      if (value) document.body.style.setProperty(name, value, priority);
      else document.body.style.removeProperty(name);
    });
    if (state.overflow) root.style.setProperty('overflow', state.overflow, state.overflowPriority);
    else root.style.removeProperty('overflow');
    const behavior = root.style.getPropertyValue('scroll-behavior');
    const priority = root.style.getPropertyPriority('scroll-behavior');
    root.style.setProperty('scroll-behavior', 'auto', 'important');
    window.scrollTo(state.x, state.y);
    if (behavior) root.style.setProperty('scroll-behavior', behavior, priority);
    else root.style.removeProperty('scroll-behavior');
  };

  const showImage = index => {
    currentIndex = (index + triggers.length) % triggers.length;
    const trigger = triggers[currentIndex];
    const token = ++requestId;
    const image = viewer.querySelector('[data-lightbox-image]');
    const stage = viewer.querySelector('[data-lightbox-stage]');
    const status = viewer.querySelector('[data-lightbox-status]');
    const error = viewer.querySelector('[data-lightbox-error]');
    const caption = trigger.dataset.lightboxCaption || trigger.querySelector('img')?.alt || '';
    const imageUrl = trigger.dataset.lightboxMobileSrc && matchMedia(trigger.dataset.lightboxMobileMedia || '(max-width: 749px)').matches ? trigger.dataset.lightboxMobileSrc : trigger.href;
    image.hidden = true;
    image.removeAttribute('src');
    image.alt = caption;
    error.hidden = true;
    status.textContent = 'Loading image…';
    stage.setAttribute('aria-busy', 'true');
    viewer.querySelector('[data-lightbox-caption]').textContent = caption;
    viewer.querySelector('[data-lightbox-counter]').textContent = `${currentIndex + 1} / ${triggers.length}`;
    viewer.querySelector('[data-lightbox-original]').href = imageUrl;
    viewer.querySelector('[data-lightbox-previous]').hidden = triggers.length < 2;
    viewer.querySelector('[data-lightbox-next]').hidden = triggers.length < 2;

    // Only commit the latest completed request: rapid swipes cannot show an old image.
    const preload = new Image();
    preload.onload = () => {
      if (token !== requestId || !viewer.open) return;
      image.width = preload.naturalWidth;
      image.height = preload.naturalHeight;
      image.src = preload.src;
      image.hidden = false;
      status.textContent = '';
      stage.setAttribute('aria-busy', 'false');
    };
    preload.onerror = () => {
      if (token !== requestId || !viewer.open) return;
      error.hidden = false;
      stage.setAttribute('aria-busy', 'false');
      status.textContent = '';
    };
    preload.src = imageUrl;
  };

  const close = () => {
    if (!viewer?.open || viewer.classList.contains('is-closing')) return;
    ++requestId;
    viewer.classList.add('is-closing');
    if (reducedMotion.matches) viewer.close();
    else closeTimer = setTimeout(() => viewer.close(), 140);
  };

  const initialize = dialog => {
    if (initialized.has(dialog)) return;
    initialized.add(dialog);
    const stage = dialog.querySelector('[data-lightbox-stage]');
    dialog.querySelector('[data-lightbox-close]').addEventListener('click', close);
    dialog.querySelector('[data-lightbox-previous]').addEventListener('click', () => showImage(currentIndex - 1));
    dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => showImage(currentIndex + 1));
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    dialog.addEventListener('close', () => {
      clearTimeout(closeTimer);
      ++requestId;
      dialog.classList.remove('is-closing');
      dialog.querySelector('[data-lightbox-image]').hidden = true;
      unlockScroll();
      if (returnFocus?.isConnected) returnFocus.focus({preventScroll: true});
    });
    dialog.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || triggers.length < 2 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      showImage(currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    dialog.addEventListener('click', event => {
      if (performance.now() >= ignoreBackdropClickUntil && (event.target === dialog || event.target === stage)) close();
    });

    // Observe one-finger gestures without preventing native pinch zoom or vertical pan.
    const pointers = new Set();
    let swipe;
    let multiTouch = false;
    stage.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch') return;
      pointers.add(event.pointerId);
      if (pointers.size > 1) { multiTouch = true; swipe = null; return; }
      multiTouch = false;
      if ((window.visualViewport?.scale || 1) > 1.05) return;
      swipe = {id: event.pointerId, x: event.clientX, y: event.clientY, time: event.timeStamp};
    }, {passive: true});
    const endSwipe = event => {
      pointers.delete(event.pointerId);
      if (event.type === 'pointerup' && !multiTouch && swipe?.id === event.pointerId && triggers.length > 1 && (window.visualViewport?.scale || 1) <= 1.05) {
        const dx = event.clientX - swipe.x;
        const dy = event.clientY - swipe.y;
        if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.8 && event.timeStamp - swipe.time < 800) {
          ignoreBackdropClickUntil = performance.now() + 400;
          showImage(currentIndex + (dx < 0 ? 1 : -1));
        }
      }
      swipe = null;
      if (!pointers.size) multiTouch = false;
    };
    stage.addEventListener('pointerup', endSwipe, {passive: true});
    stage.addEventListener('pointercancel', endSwipe, {passive: true});
  };

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const trigger = event.target.closest?.(triggerSelector);
    const dialog = document.querySelector('[data-holubo-lightbox-dialog]');
    if (!trigger || !dialog || typeof dialog.showModal !== 'function' || !trigger.getAttribute('href')) return;
    event.preventDefault();
    if (dialog.open) return;
    viewer = dialog;
    initialize(dialog);
    returnFocus = trigger;
    const group = trigger.dataset.lightboxGroup;
    triggers = group ? [...document.querySelectorAll(triggerSelector)].filter(item => item.dataset.lightboxGroup === group && item.getAttribute('href')) : [trigger];
    lockScroll();
    try { dialog.showModal(); }
    catch { unlockScroll(); window.location.assign(trigger.href); return; }
    showImage(triggers.indexOf(trigger));
    dialog.querySelector('[data-lightbox-close]').focus({preventScroll: true});
  });

  document.addEventListener('shopify:section:unload', event => {
    if (viewer?.open && event.target.contains(returnFocus)) close();
  });
})();
